import React, { useEffect } from "react";
import ClosableModal from "components/source/shared/closable-modal";
import { PdpImageZoomModalName } from "rtr-constants";
import { useDispatch, useSelector } from "react-redux";
import SharedActions from "actions/shared-actions";
import PDPImagery from "../pdp/pdp-imagery";
import PropTypes from "prop-types";

const PdpImageZoomModal = ({ altText, images, imageRotation, isMobileViewport, openingElement }) => {
  const dispatch = useDispatch();
  const displayedModal = useSelector(state => state.displayedModal);

  useEffect(() => {
    if (isMobileViewport) {
      scrollToImage(openingElement?.index, null, true);
    } else {
      zoomOpenEffect();
    }
  }, [openingElement?.ref, displayedModal]);

  useEffect(() => {
    // on unmount, ensure the copied image is definitely removed
    return () => {
      if (document?.getElementById("pdp-img-copy")) {
        document.body.removeChild(document.getElementById("pdp-img-copy"));
      }
    };
  }, []);

  const closeModal = () => dispatch(SharedActions.displayModal(false));

  const zoomOpenEffect = () => {
    const imageToZoomOn = openingElement?.ref?.current;

    if (imageToZoomOn && displayedModal === PdpImageZoomModalName) {
      const copiedElement = imageToZoomOn.cloneNode(true);
      copiedElement.id = `pdp-img-copy`;
      copiedElement.setAttribute("data-test-id", "pdp-img-copy");
      copiedElement.style.position = "absolute";

      const rect = imageToZoomOn.getBoundingClientRect();

      // set the copied element's position to match the original's position
      const scrollTop = document.documentElement.scrollTop;
      const scrollLeft = document.documentElement.scrollLeft;
      copiedElement.style.top = `${rect.top + scrollTop}px`;
      copiedElement.style.left = `${rect.left + scrollLeft}px`;
      copiedElement.style.transform = `translate(${rect.left + scrollLeft}px, ${rect.top + scrollTop}px)`;
      copiedElement.style.width = `${rect.width}px`;
      copiedElement.style.zIndex = 999999; // ensure on top of modal

      // set the scale factor
      const scaleFactor = window.innerWidth / rect.width;
      copiedElement.style.setProperty("--scale-factor", scaleFactor);

      // calculate center position
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const translateX = viewportWidth / 2 - rect.width / 2 - rect.left;
      const translateY = viewportHeight / 2 - rect.height / 2 - rect.top;
      copiedElement.style.setProperty("--translate-x", `${translateX}px`);
      copiedElement.style.setProperty("--translate-y", `${translateY}px`);

      // apply animation
      copiedElement.style.animation = "pdpScaleToViewport .6s ease forwards";
      document.body.appendChild(copiedElement);

      setTimeout(() => {
        const modalImagesScrollWindow = document.querySelector(".pdp-image-zoom-modal");
        const imageRect = copiedElement.getBoundingClientRect();
        const offset = imageRect.top < 0 ? Math.abs(imageRect.top) : 0;
        const scrollAmount = offset + openingElement.index * imageRect.height;

        modalImagesScrollWindow.scrollTo({ top: scrollAmount, behavior: "auto" });
      }, 750);

      setTimeout(() => {
        const thumbnailContainer = document.querySelector(".pdp-thumbnails");
        thumbnailContainer.classList.add("zoom-animation-finished");
        document.body.removeChild(copiedElement);
      }, 800);
    }
  };

  const thumbnailSelected = index => {
    const thumbnailContainer = document.querySelector(".pdp-thumbnails");
    const thumbnailElements = thumbnailContainer.querySelectorAll(".featured-image");
    thumbnailElements.forEach(element => element.classList.remove("selected"));
    thumbnailElements[index].classList.add("selected");
  };

  const scrollToImage = (index, el, firstOpen = false) => {
    setTimeout(() => {
      const modalImagesContainer = document?.querySelector(".pdp-modal-images");
      const modalImagesElements = modalImagesContainer?.querySelectorAll(".featured-image");
      modalImagesElements?.[index]?.scrollIntoView({
        behavior: firstOpen ? "auto" : "smooth",
        block: "start",
      });
    }, 100);
  };

  const renderPDPImagery = ({ className = "", onClick, onVisible, altTextDetails = "image" }) => (
    <PDPImagery
      altText={`${altText} ${altTextDetails}`}
      className={className}
      isCloudZoomEnabled={false}
      images={images}
      imageRotation={imageRotation}
      isMobileViewport={isMobileViewport}
      lazy={true}
      onClick={onClick}
      onVisible={onVisible}
      isZoomedIn={true}
    />
  );

  const additionalStyles = {
    content: {
      background: "none",
      height: "100%",
    },
    overlay: {
      background: "none",
      height: "100%",
    },
  };

  return (
    <ClosableModal
      isOpen={displayedModal === PdpImageZoomModalName}
      isFullScreen={true}
      onRequestClose={closeModal}
      optionalClass="modal-close-circle no-padding full-height-on-mobile"
      optionalWrapperClass="pdp-image-zoom-modal"
      additionalStyles={additionalStyles}>
      <div data-test-id="pdp-image-zoom-modal">
        {renderPDPImagery({
          className: "pdp-modal-images",
          onVisible: index => {
            thumbnailSelected(index);
          },
        })}
        {renderPDPImagery({ className: "pdp-thumbnails", onClick: scrollToImage, altTextDetails: "thumbnail" })}
      </div>
    </ClosableModal>
  );
};
PdpImageZoomModal.propTypes = {
  altText: PropTypes.string,
  imageRotation: PropTypes.array.isRequired,
  images: PropTypes.object.isRequired,
  isMobileViewport: PropTypes.bool,
  openingElement: PropTypes.object,
};

PdpImageZoomModal.MODAL_NAME = PdpImageZoomModalName;

export default PdpImageZoomModal;
